import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import deleteFavorite from '@cartwhl/common/api/deleteFavorite';
import fetchGalleryRecords from '@cartwhl/common/api/fetchGalleryRecords';
import { getAllMediaExpanded } from '@cartwhl/common/api/getAllMedia';
import getJobs from '@cartwhl/common/api/getJobs';
import postFavorite from '@cartwhl/common/api/postFavorite';
import {
  ExpandedMediaRecordFromAPI,
  Job,
  PaginatedAsyncState,
} from '@cartwhl/common/types';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';

import { noticesStore } from './global';

// #region STORE SETUP
export type GalleryStoreState = {
  galleryRecords: PaginatedAsyncState<Job[]>;
  favoritesRecords: PaginatedAsyncState<Job[]>;
  mediaRecords: PaginatedAsyncState<ExpandedMediaRecordFromAPI[]>;
};

export const state = proxy<GalleryStoreState>({
  galleryRecords: { status: 'initial', data: undefined },
  favoritesRecords: { status: 'initial', data: undefined },
  mediaRecords: { status: 'initial', data: undefined },
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Gallery', enabled: true });
}
// #endregion

// #region Gallery
export async function fetchGallery(
  limit?: number,
  sortAscending?: boolean,
  global?: boolean,
) {
  if (state.galleryRecords.status === 'pending') return;

  // Keep existing data if loading more, otherwise set to pending
  const existingItems = state.galleryRecords.data?.items || [];
  const nextToken = state.galleryRecords.data?.nextToken;

  state.galleryRecords = {
    status: 'pending',
    data:
      existingItems.length > 0
        ? {
            items: existingItems,
            nextToken,
          }
        : undefined,
  };

  const { data, error } = await fetchGalleryRecords({
    global,
    limit,
    sortAscending,
    nextToken,
  });

  if (error) {
    await handleGenericErrors(error);
    state.galleryRecords = {
      status: 'error',
      error: error.message,
      data: undefined,
    };
  } else if (data) {
    const allItems = existingItems.concat(data.jobs);
    state.galleryRecords = {
      status: 'ready',
      data: {
        items: allItems,
        nextToken: data.nextToken || undefined,
      },
    };
  }
}
// #endregion

// #region Favorites
export async function fetchFavorites(limit?: number, sortAscending?: boolean) {
  if (state.favoritesRecords.status === 'pending') return;

  // Keep existing data if loading more, otherwise set to pending
  const existingItems = state.favoritesRecords.data?.items || [];
  const nextToken = state.favoritesRecords.data?.nextToken;

  state.favoritesRecords = {
    status: 'pending',
    data:
      existingItems.length > 0
        ? {
            items: existingItems,
            nextToken,
          }
        : undefined,
  };

  const { data, error } = await getJobs(
    limit,
    nextToken,
    !!sortAscending,
    true,
  );

  if (error) {
    await handleGenericErrors(error);
    state.favoritesRecords = {
      status: 'error',
      error: error.message,
      data: undefined,
    };
  } else if (data?.jobs) {
    const allItems = existingItems.concat(data.jobs);
    state.favoritesRecords = {
      status: 'ready',
      data: {
        items: allItems,
        nextToken: data.nextToken || undefined,
      },
    };
  }
}
export async function setFavorite(
  jobID: string,
  animationNumber: string | number,
  desiredStatus: boolean,
) {
  if (desiredStatus) {
    return await postFavorite(jobID, +animationNumber).then(
      ({ data, error }) => {
        if (error) {
          handleGenericErrors(error);
        } else if (data) {
          const items = state.favoritesRecords.data?.items || [];
          const job = items.find((job) => job.jobID === jobID);
          // TODO: Job likely doesn't exist. Need to figure out a workaround
          if (job) {
            job.variationInfoByID[animationNumber].favorite = true;
          }
          noticesStore.success('Added to favorites');
        }
        return { data, error };
      },
    );
  } else {
    return await deleteFavorite(jobID, +animationNumber).then(
      ({ data, error }) => {
        if (error) {
          handleGenericErrors(error);
        } else if (data) {
          const items = state.favoritesRecords.data?.items || [];
          const job = items.find((job) => job.jobID === jobID);
          if (job) {
            job.variationInfoByID[animationNumber].favorite = false;
            delete job.variationInfoByID[animationNumber];
          }
          noticesStore.success('Removed from favorites');
        }
        return { data, error };
      },
    );
  }
}

export function hasFavorites() {
  const items = state.favoritesRecords.data?.items || [];
  for (let i = 0; i < items.length; i++) {
    const job = items[i];
    if (Object.keys(job.variationInfoByID).length > 0) {
      return true;
    }
  }
  return false;
}
// #endregion

// #region Media
export async function fetchMediaRecords() {
  if (state.mediaRecords.status === 'pending') return;

  state.mediaRecords = { status: 'pending', data: undefined };

  const { data, error } = await getAllMediaExpanded();

  if (error) {
    await handleGenericErrors(error);
    state.mediaRecords = {
      status: 'error',
      error: error.message,
      data: undefined,
    };
  } else if (data) {
    // Since getAllMediaExpanded doesn't use pagination, we set nextToken to undefined (no more pages)
    state.mediaRecords = {
      status: 'ready',
      data: {
        items: data,
        nextToken: undefined,
      },
    };
  }
}
// #endregion
